import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiCreditCard, FiCheckCircle, FiAlertCircle, FiDollarSign } from 'react-icons/fi';
import Button from './Button';
import Input from './Input';
import { paymentAPI } from '../../api/paymentEndpoints';
import { showToast } from './Toaster';
import { formatCurrency } from '../../utils/helpers';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme.colors.backgroundSecondary};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const CourseInfo = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  padding: 16px;
  background: ${props => props.theme.colors.backgroundSecondary};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.colors.border};
`;

const CourseThumbnail = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const CourseThumbnailPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  flex-shrink: 0;
`;

const CourseDetails = styled.div`
  flex: 1;
`;

const CourseTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 8px 0;
`;

const CoursePrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PaymentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StatusContainer = styled.div`
  text-align: center;
  padding: 32px 24px;
`;

const StatusIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.success ? '#10B98115' : props.pending ? '#F59E0B15' : '#EF444415'};
  color: ${props => props.success ? '#10B981' : props.pending ? '#F59E0B' : '#EF4444'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 32px;
`;

const StatusTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 8px 0;
`;

const StatusMessage = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const PurchaseCourseModal = ({ isOpen, onClose, course, onSuccess }) => {
  const [msisdn, setMsisdn] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'pending', 'success', 'failed'
  const [checkoutRequestId, setCheckoutRequestId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchUserPhone();
      setPaymentStatus(null);
      setCheckoutRequestId(null);
    }
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isOpen]);

  const fetchUserPhone = async () => {
    try {
      setVerifying(true);
      const response = await paymentAPI.getUserVerification();
      if (response.data?.msisdn) {
        setMsisdn(response.data.msisdn);
      }
    } catch (error) {
      console.error('Failed to fetch user phone:', error);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!msisdn || msisdn.length < 12) {
      showToast('error', 'Please enter a valid phone number (254XXXXXXXXX)');
      return;
    }

    if (!course || !course.id || !course.price) {
      showToast('error', 'Invalid course information');
      return;
    }

    try {
      setLoading(true);
      const response = await paymentAPI.purchaseCourse(course.id, course.price, msisdn);
      
      setCheckoutRequestId(response.checkout_request_id);
      setPaymentStatus('pending');
      showToast('info', 'Payment request sent. Please check your phone to complete the payment.');
      
      // Start polling for payment status
      startStatusPolling(response.checkout_request_id);
    } catch (error) {
      console.error('Purchase error:', error);
      const errorMessage = error.message || 'Failed to initiate purchase. Please try again.';
      showToast('error', errorMessage);
      setPaymentStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  const startStatusPolling = (requestId) => {
    let attempts = 0;
    const maxAttempts = 60; // Poll for 2 minutes (60 * 2 seconds)

    const interval = setInterval(async () => {
      attempts++;
      
      try {
        const response = await paymentAPI.getPaymentStatus(requestId);
        const status = response.data?.status;

        if (status === 'success') {
          clearInterval(interval);
          setPaymentStatus('success');
          showToast('success', 'Course purchased successfully!');
          if (onSuccess) {
            onSuccess(course.id);
          }
        } else if (status === 'failed' || status === 'cancelled') {
          clearInterval(interval);
          setPaymentStatus('failed');
          showToast('error', response.data?.result_desc || 'Payment failed');
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
          setPaymentStatus('failed');
          showToast('error', 'Payment verification timeout. Please check your payment status.');
        }
      } catch (error) {
        console.error('Status check error:', error);
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          setPaymentStatus('failed');
        }
      }
    }, 2000);

    setPollingInterval(interval);
  };

  const handleClose = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    onClose();
  };

  if (!isOpen || !course) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            {paymentStatus === 'success' ? 'Purchase Successful!' : 'Purchase Course'}
          </ModalTitle>
          <CloseButton onClick={handleClose}>
            <FiX size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {paymentStatus === null && (
            <>
              <CourseInfo>
                {course.thumbnail_url ? (
                  <CourseThumbnail src={course.thumbnail_url} alt={course.title} />
                ) : (
                  <CourseThumbnailPlaceholder>📚</CourseThumbnailPlaceholder>
                )}
                <CourseDetails>
                  <CourseTitle>{course.title}</CourseTitle>
                  <CoursePrice>
                    <FiDollarSign size={20} />
                    KES {formatCurrency(course.price)}
                  </CoursePrice>
                </CourseDetails>
              </CourseInfo>

              <InfoText>
                After purchasing, you'll have <strong>lifetime access</strong> to this course, including all videos, materials, and quizzes.
              </InfoText>

              <PaymentForm onSubmit={handleSubmit}>
                <Input
                  label="M-Pesa Phone Number"
                  type="tel"
                  value={msisdn}
                  onChange={(e) => setMsisdn(e.target.value)}
                  placeholder="254712345678"
                  disabled={verifying || loading}
                  icon={<FiCreditCard />}
                  required
                />

                <Button
                  type="submit"
                  loading={loading}
                  disabled={verifying || loading || !msisdn}
                  fullWidth
                >
                  <FiCreditCard /> Pay KES {formatCurrency(course.price)}
                </Button>
              </PaymentForm>
            </>
          )}

          {paymentStatus === 'pending' && (
            <StatusContainer>
              <StatusIcon pending>
                <div className="animate-spin">⏳</div>
              </StatusIcon>
              <StatusTitle>Processing Payment...</StatusTitle>
              <StatusMessage>
                Please check your phone and enter your M-Pesa PIN to complete the payment.
                This may take a few moments.
              </StatusMessage>
            </StatusContainer>
          )}

          {paymentStatus === 'success' && (
            <StatusContainer>
              <StatusIcon success>
                <FiCheckCircle />
              </StatusIcon>
              <StatusTitle>Payment Successful!</StatusTitle>
              <StatusMessage>
                You now have lifetime access to this course. Start learning now!
              </StatusMessage>
              <Button
                onClick={handleClose}
                style={{ marginTop: '24px' }}
              >
                Start Learning
              </Button>
            </StatusContainer>
          )}

          {paymentStatus === 'failed' && (
            <StatusContainer>
              <StatusIcon>
                <FiAlertCircle />
              </StatusIcon>
              <StatusTitle>Payment Failed</StatusTitle>
              <StatusMessage>
                The payment could not be completed. Please try again or contact support if the problem persists.
              </StatusMessage>
              <Button
                onClick={() => {
                  setPaymentStatus(null);
                  setCheckoutRequestId(null);
                }}
                style={{ marginTop: '24px' }}
              >
                Try Again
              </Button>
            </StatusContainer>
          )}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default PurchaseCourseModal;


